import z from "zod";
import { AttachmentResultType, Hours, PriorityType } from "./common";
import {
  ChecklistInput,
  ResourceAutocompleteOptionsType,
  SlaTrackingData,
} from "./configuration";
import type { TicketEscalatedMember } from "./escalations";
export interface CreateTicketInput {
  isBillable: boolean;
  checklist: Array<ChecklistInput>;
  board?: string;
  priority?: string;
  status?: string;
  summary: string;
  description: string;
  resources?: Array<ResourceAutocompleteOptionsType>;
  urgency?: string;
  workType?: string;
  ticketType?: string;
  ticketSubtype?: string;
  source?: string;
  impact?: string;
  sla?: string;
  customer?: string;
  site?: string;
  ticket_site_id?: string;
  user?: string;
  schedule?: Array<Schedule>;
  location?: string;
  generalVariable?: Array<Attribute>;
  ticketVariable?: Array<Attribute>;
  budgetedHours?: string;
  attachments?: Array<AttachmentResultType>;
  estimatedEndTime?: string;
  tags?: Array<object> | null;
  contract?: string;
  /** When true, backend creates the immediate ticket AND schedules a recurring run
   *  in the same call (tickets screen → "Make this ticket recurring" flow). */
  is_recurring_ticket?: boolean;
  frequency?: string;
  /** "HH:MM" 24h. */
  creation_time?: string;
  /** ISO date "YYYY-MM-DD". */
  start_date?: string;
  end_date?: string | null;
  /** IANA tz. */
  time_zone?: string;
  ccMembers?: Array<object> | null;
  /**
   * AAC-577 — posted by the backend as a private chat message on the new
   * ticket, authored by the caller. Omit (or send blank) for no note.
   */
  privateNote?: string;
}

/**
 * AAC-577 — what happened to the private note sent with a create. The ticket is
 * created either way; `posted: false` fails only the note.
 *
 * Both flags are optional because the live response has drifted from the
 * contract: the contract documents `{ posted: true, chat_id }`, but UAT returns
 * `{ queued: true, chat_id }` since 2026-10-12, the note landing ~8 s later. The
 * failure shape under the queued flow is unconfirmed with backend.
 */
export interface CreateTicketPrivateNoteResult {
  posted?: boolean;
  /** UAT's current success shape — the note is written asynchronously. */
  queued?: boolean;
  /** The chat message id the note will have. */
  chat_id?: string;
  /** Human-readable reason, when not posted. */
  error?: string;
}

/** `createTicket`'s result: the created ticket, plus the note outcome if one was sent. */
export interface CreateTicketResult {
  /** The created ticket — the route's `data`, untyped upstream. */
  ticket: unknown;
  /** Present only when a private note was sent. */
  privateNote?: CreateTicketPrivateNoteResult;
}

export interface CreateChildTicketInput extends CreateTicketInput {
  ticketId: string;
}
export interface Attribute {
  attribute_id: string;
  attribute_value_id: string;
}
export interface Schedule {
  startDate: string | null;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
}

export interface ParentTicketOptionProps {
  value: string;
  ticket_summary: string;
  customer_name: string;
  u_id: string;
  label: string;
}
export interface AllTicketResponse {
  customer_id: any;
  mapping_id: string;
  u_id: string;
  ticket_summary: string;
  ticket_description: string;
  ticket_status: string;
  status_name: string;
  priority: string;
  urgency: string;
  impact: string;
  connect_time_from?: string | null;
  connect_time_to?: string | null;
  is_billable: boolean;
  msp_id: number;
  board_name: string;
  work_type_name: string;
  source_name: string;
  location_name?: string | null;
  sla_name?: string | null;
  created_at: string;
  feedback?: string | null;
  rating?: number | null;
  type_name: string;
  subtype_name: string;
}

export interface TicketListBaseObject {
  id: string;
  mapping_id: string;
  u_id: string;
  ticket_summary: string;
  ticket_description: string;
  ticket_status: string;
  status_name?: string;
  board: string;
  priority: string;
  msp_id: string;
  is_billable: boolean;
  urgency: string | null;
  work_type: string | null;
  work_role: string | null;
  ticket_type: string;
  source: string;
  impact: string | null;
  location: string | null;
  budgeted_hours: Hours;
  scheduled_hours: number | null;
  actual_hours: number | null;
  sla: string;
  child_to: string | null;
  merged_to: string | null;
  customer_id: string;
  site_id: string;
  ticket_site_id: string;
  customer_user_id: string;
  ticket_owner: string | null;
  connect_time_from: string | null;
  connect_time_to: string | null;
  due_date: string | null;
  estimated_end_date: string | null;
  end_date: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
  is_deleted: boolean;
  mail_message_id: string | null;
  mail_subject: string | null;
  is_expired_sla: boolean;
  closed_by: string | null;
  meta: any; // Replace 'any' with a more specific type if the structure of meta is known
  is_known_user: boolean | null;
  is_archived: boolean;
  archived_by: string | null;
  archived_at: string | null;
  amount: number;
  contract_id: string;
  closed_at: string | null;
  service_platform: string;
  invoice_status: string | null;
  is_agent_visible: boolean;
  bill_after_closed: boolean;
  bill_after_timesheet_approval: boolean;
  bill_products: boolean;
  bill_expenses: boolean;
  ticket_subtype: string;
}

export interface AttachmentProps {
  mapping_id: string;
  original_name: string;
  file_type: string;
  url: string;
}

export interface ServiceType {
  serviceType: "ticket" | "request" | "project" | string;
}

export interface ActiveTimeEntry {
  mapping_id: string;
  service_id: string;
  service_type: "ticket" | "request" | "project" | "task";
  start_time: string;
  activeTimerValueInSeconds?: number;
  service_summary?: string;
  u_id: string;
  customer_id?: string;
  customer_user_id?: string;
  customer_name?: string;
  customer_user_name?: string;
  email?: string;
  profile_image?: string;
  is_privileged_use?: boolean;
  project_id?: string;
}

export interface TimeEntryInfo {
  mapping_id: string;
  service_id: string;
  service_type: "ticket" | "request" | "project" | "task";
}

export interface RawActiveTimeEntry extends ActiveTimeEntry {
  customer_user_first_name?: string;
  customer_user_middle_name?: string;
  customer_user_last_name?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
}

export interface Watchlist {
  service_id: string;
  service_type: "ticket" | "request" | "project" | "task";
  service_summary?: string;
  u_id: string;
  customer_name?: string;
  customer_user_name?: string;
  is_privileged_use?: boolean;
  profile_image?: string;
  project_id?: string;
}

export interface createTimeEntriesInput extends ServiceType {
  resourceId: string;
  serviceId: string;
  startTime: string;
  endTime: string;
  isBillable: boolean;
  notes: string;
  deductTime: string;
  accessSpecifier: "public" | "private" | "self" | string;
  markAsResolution: boolean;
  notesAttachments: Array<AttachmentProps>;
  sendAsEmail: boolean;
  work_type?: string;
  work_role?: string;
  is_operating_hours?: boolean;
}

export interface UpdatetimeEntriesInput extends createTimeEntriesInput {
  timeEntryId: string;
}

export interface startTimerInput extends ServiceType {
  serviceId: string;
  resourceId: string;
}

export interface endTimerInput extends ServiceType {
  serviceId: string;
  timeEntryId: string;
}

// export interface TicketData {
//   mapping_id: string;
//   u_id: string;
//   ticket_summary: string;
//   ticket_description: string;
//   ticket_status: string;
//   status_name: string;
//   priority: string;
//   urgency: string;
//   impact: string;
//   connect_time_from: null;
//   connect_time_to: null;
//   is_billable: boolean;
//   msp_id: number;
//   budgeted_hours: TicketDataBudgetedHours;
//   scheduled_hours: ActualHours;
//   actual_hours: ActualHours;
//   board_id: string;
//   board_name: string;
//   estimated_end_date: string;
//   work_type: string;
//   work_type_name: string;
//   work_role: null;
//   work_role_name: null;
//   source_name: string;
//   location_name: string;
//   sla_name: string;
//   created_at: string;
//   profile_image: null;
//   first_name: string;
//   last_name: string;
//   display_name: string;
//   email: string;
//   ticketVariableAttribute: TicketVariableAttribute[];
//   generalVariableAttribute: GeneralVariableAttribute[];
//   checklists: Checklist[];
//   childTickets: ChildTicket[];
//   attachments: Attachment[];
//   mergeTickets: any[];
//   schedules: Schedule[];
//   tags: Tag[];
//   ticketTypeSubtype: TicketTypeSubtype[];
//   timeEntries: any[];
// }

// export interface ActualHours {
//   hours: number;
//   minutes: number;
//   seconds: number;
// }

// export interface Attachment {
//   id: string;
//   mapping_id: null;
//   ticket_id: number;
//   service_files_id: number;
//   created_by: null;
//   created_at: string;
//   updated_at: null;
//   updated_by: null;
//   is_deleted: boolean;
// }

// export interface TicketDataBudgetedHours {
//   minutes: number;
// }

// export interface Checklist {
//   id: string;
//   mapping_id: string;
//   ticket_id: number;
//   checklist_item_name: string;
//   order: number;
//   is_completed: boolean;
//   is_important: boolean;
//   created_by: null;
//   created_at: string;
//   updated_at: null;
//   updated_by: null;
//   is_deleted: boolean;
// }

export interface ChildTicketType {
  id: string;
  mapping_id: string;
  u_id?: string;
  ticket_name?: string;
  ticket_summary: string;
  ticket_description: string;
  ticket_status: string;
  board: string;
  priority: string;
  msp_id: number;
  is_billable: boolean;
  urgency: string;
  work_type: string;
  work_role: null;
  ticket_type?: string;
  source: string;
  impact: string;
  location: string;
  budgeted_hours?: Hours;
  scheduled_hours?: Hours | null;
  actual_hours?: Hours | null;
  sla: string;
  child_to?: number | null;
  merged_to?: number | null;
  customer_id?: any; // TODO: check this
  site_id?: null;
  ticket_site_id?: string | null;
  customer_user_id: null;
  connect_time_from: null;
  connect_time_to: null;
  due_date: string;
  estimated_end_date: null;
  end_date: null;
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  is_deleted: boolean;
  ticket_owner?: null | string;
}

// export interface GeneralVariableAttribute {
//   mapping_id: null | string;
//   general_variable_attribute_name: null | string;
//   general_variable_attribute_value_name: null | string;
// }

// export interface Schedule {
//   mapping_id: string;
//   resource_id: string;
//   service_type: string;
//   service_id: string;
//   start_time: string;
//   end_time: string;
//   duration: ActualHours;
//   is_completed: null;
//   profile_image: null;
//   first_name: string;
//   last_name: string;
//   display_name: string;
//   email: string;
// }

// export interface Tag {
//   mapping_id: string;
//   tag_name: string;
// }

// export interface TicketTypeSubtype {
//   ticket_type_id: string;
//   type_name: string;
//   ticket_subtype_id: string;
//   subtype_name: string;
// }

// export interface TicketVariableAttribute {
//   mapping_id: null | string;
//   ticket_variable_attribute_name: null | string;
//   ticket_variable_attribute_value_name: null | string;
// }

export interface MergeTicketInput {
  parentTcketId: string;
  mergeTickets: Array<string>;
}

export interface BulkAssignTicketInputProps {
  service_id: string[];
  resource_id: string[];
  note: string;
}

// export interface ModifyTicketInputProps{
//   ticket_ids: string [];
//   tags: string [];
//   board: string;

// }

export interface ChildTicketInput {
  parentTcketId: string;
  childTickets: Array<string>;
}

export interface Attachment {
  mapping_id: string;
  original_name: string;
  url: string;
  file_type: string;
  size: number;
  attachment_type?: string;
}

export interface SingleChecklistResult {
  id?: number;
  mapping_id?: string;
  ticket_id?: 38;
  checklist_item_name: string;
  order: number;
  is_completed?: boolean;
  is_important: boolean;
  created_by?: any; // TODO: fix any
  created_at?: string;
  updated_at?: string | null;
  updated_by?: any | null;
  completed_by?: any | null;
  completed_at?: string | null;
  is_deleted?: boolean;
  first_name?: string;
  last_name?: string;
}

export interface ScheduleResult {
  mapping_id: string;
  resource_id: string;
  service_type: string;
  service_id: string;
  start_time?: string | null;
  end_time?: string | null;
  duration: Hours;
  is_completed?: boolean | null;
  profile_image: string | null;
  first_name: string;
  last_name: string;
  display_name: string | null;
  email: string;
}

export interface AddScheduleInput {
  serviceId: string;
  serviceType: "ticket" | "request" | "task" | "project";
  schedules: Array<{
    resource_id: string;
    schedule?: Array<{
      start_time: string;
      end_time: string;
    }>;
    schedule_times?: Array<{
      start_time: string;
      end_time: string;
    }>;
  }>;
}

export interface AddTaskSchedule extends Omit<AddScheduleInput, "ticketId"> {
  taskId: string;
}
export interface UpdateAssigneesInput {
  serviceId: string;
  serviceType: string;
  scheduleId: string;
  scheduleData?: {
    start_time?: string | null;
    end_time?: string | null;
    is_completed?: boolean | null;
  };
}

export interface DeleteAssigneesInput {
  serviceId: string;
  serviceType: "ticket" | "request" | "task" | "project";
  scheduleId: string;
}
// export interface ScheduleTimes {
//   start_time: string;
//   end_time: string;
// }

export interface Assignees {
  mapping_id: string;
  first_name: string;
  last_name: string | null;
  display_name: string | null;
  profile_image: string | null;
}

export interface ChangeBillableStatusInput {
  ticketId: string;
  newValue: boolean;
}
export interface UpdateTicketStatusInput {
  ticketId: string;
  statusId: string;
}

export interface UpdateTicketStatusResponse extends TicketListBaseObject {}

export interface UpdateTicketPriorityInput {
  ticketId: string;
  priority: PriorityType;
}

export interface UpdateTicketPriorityResponse extends TicketListBaseObject {}

export interface UpdateChecklistItmeInput {
  serviceType?: "ticket" | "request" | "tasks";
  serviceId: string;
  checklistId: string;
  checklist_item_name?: string;
  // "order"?: number,
  is_important?: boolean;
  is_completed?: boolean;
}
export interface UpdateChecklistInput {
  serviceType?: "ticket" | "request" | "tasks";
  serviceId: string;
  checklistArray: Array<any>;
}

export interface UpdateAdditionalTicketDetailsInput {
  ticketSummary?: string;
  ticketDescription?: string;
  ticketId?: string;
  workType?: string;
  workRole?: string;
  source?: string;
  location?: string;
  budgetedHours?: string;
  estimatedEndDate?: string;
  ticketType?: string;
  ticketSubtype?: string;
  ticketVariableAttribute?: Array<any>;
  generalVariableAttribute?: Array<any>;
  tags?: Array<any>;
  ticketOwner?: string;
  isSlaExpired?: boolean;
  customerId?: string;
  siteId?: string;
  ticketSiteId?: string;
  userId?: string;
  board?: string;
  impact?: string;
  urgency?: string;
  sla?: string;
  attachments?: Array<any>;
  deviceId?: string | null;
  privateNote?: string;
}

export interface ticketDetailsFromTimeEntriesInput {
  ticketId: string;
  workType?: string;
  isBillable?: boolean;
  status?: string;
}

export interface DeleteTicketAttachmentInput {
  ticketId: string;
  attachmentId: Array<string>;
}

export interface DeleteTimeEntryInput {
  ticketId: string;
  serviceType: string;
  timeEntryId: string;
}

export interface GetTimeEntryByIdInput {
  serviceType: string;
  timeEntryId: string;
}

export interface UpdateScheduleConnectTimeInput {
  ticketId: string;
  connectTimeFrom: string;
  connectTimeTo: string;
}

export interface GetSplitTimeEntriesByIdInput {
  timeEntryId: string;
}

interface TagProps {
  mapping_id: string;
  tag_name: string;
}
export interface UpdateProjectDetails {
  projectId: string;
  data: {
    project_title?: string;
    project_manager?: string;
    project_description?: string;
    project_status?: string;
    board?: string;
    priority?: string;
    is_billable?: boolean;
    urgency?: string;
    work_role?: string;
    work_type?: string;
    project_type?: string;
    project_subtype?: string;
    source?: string;
    impact?: string;
    location?: string;
    budgeted_hours?: string;
    scheduled_hours?: string;
    estimated_end_date?: string;
    customer_id?: string;
    site_id?: string;
    customer_user_id?: string;

    // project_variable_attributes: [];
    // general_variable_attributes: [];
    tags?: Array<TagProps>;
    attachments?: Array<any>;
  };
}

export interface UpdateProjectPriorityInput {
  projectId: string;
  priority: PriorityType;
}

export interface UpdateProjectStatusInput {
  projectId: string;
  status: string;
}

export interface UpdateTaskPriorityInput {
  taskId: string;
  priority: PriorityType;
}

export interface UpdateTaskDetailsInput {
  taskId: string;
  projectId?: string;
  data: {
    task_name?: string;
    task_summary?: string;
    task_owner?: string;
    task_description?: string;
    task_status?: string;
    board?: string;
    priority?: string;
    is_billable?: boolean;
    urgency?: string;
    work_role?: string;
    work_type?: string;
    task_type?: string;
    task_subtype?: string;
    source?: string;
    impact?: string;
    location?: string;
    budgeted_hours?: string;
    scheduled_hours?: string;
    estimated_end_date?: string;
    customer_id?: string;
    site_id?: string;
    customer_user_id?: string;
    // project_variable_attributes: [];
    // general_variable_attributes: [];
    tags?: Array<TagProps>;
    // attachments: [];
    show_add_description?: string;
  };
}

export interface BulkUpdateTasksInput {
  projectId: string;
  tasks: Array<BulkTaskProp>;
}

type BulkTaskProp = {
  order: number;
  mapping_id: string;
};
export interface CreateTaskInput {
  milestoneId: string;
  projectId: string;
  data: {
    taskName: string;
    order: number;
    customerId: string;
    userId: string;
    siteId: string;
    contractId: string;
  };
}

export interface BulkDeleteTaskInput {
  taskIds: Array<string>;
  projectId: string;
  milestoneId: string;
}
export interface CreateMilestoneInput {
  projectId: string;
  data: {
    milestoneName: string;
    order: number;
    customerId: string;
  };
}

export interface UpdateMilestoneInput {
  milestoneId: string;
  projectId: string;
  data: {
    milestoneName: string;
    order?: number;
  };
}

interface BulkMilestoneProp {
  mapping_id: string;
  milestone_name: string;
  order: number;
}
export interface BulkUpdateMilestoneInput {
  projectId: string;
  milestones: Array<BulkMilestoneProp>;
}

export interface UpdateProjectBillingInfoInput {
  projectId: string;
  data: {
    billing_method?: string;
    hourly_rate?: number;
    down_payment?: number;
    is_billable_time?: boolean;
    estimated_time_cost?: number;
    is_billable_expenses?: boolean;
    estimated_expense_cost?: number;
    is_billable_products?: boolean;
    estimated_product_cost?: number;
    billing_after_closed?: boolean;
    billing_unapproved_time_expenses?: boolean;
    po_amount?: number;
  };
}

export interface UpdateTaskStatusInput {
  taskId: string;
  statusId: string;
}

export interface BulkAssignResourcesInputProps {
  service_id: string[];
  resource_id: string[];
  serviceType: "ticket" | "request" | "task" | "project";
  note?: string;
}

export interface CreateTemplateFromProjectInput {
  projectId: string;
  data: {
    templateName: string;
  };
}

export const CreateActivityInputSchema = z.object({
  activityType: z.string(),
  resource_id: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  is_billable: z.boolean(),
  activity_comment: z.string().optional(),
  deduct_time: z.string(),
  customer_id: z.string(),
  activity_title: z.string(),
  activity_attachments: z.array(z.any()),
  contract_id: z.string(),
});

export type CreateActivityInputType = z.infer<typeof CreateActivityInputSchema>;

const UpdateActivitySchema = CreateActivityInputSchema.omit({
  activityType: true,
  resource_id: true,
}).extend({
  entryId: z.string(),
  activity_type: z.string(),
});

export type UpdateActivityInputType = z.infer<typeof UpdateActivitySchema>;

const UpdateActivityByManagerSchema = CreateActivityInputSchema.omit({
  activityType: true,
}).extend({
  entryId: z.string(),
  activity_type: z.string(),
});

export type UpdateActivityByManagerInputType = z.infer<
  typeof UpdateActivityByManagerSchema
>;
/**
 * ⚠️ NOT the `bulk/delete/assignee` endpoint — this drives
 * `PATCH /servicedesk/{type}/bulk/assign`, which the Projects header uses.
 * The name predates the dedicated bulk-delete route below; see
 * `BulkDeleteSchedulesInput`.
 */
export interface BulkDeleteAssigneesInput {
  serviceType: "ticket" | "request" | "task" | "project";
  schedules: Array<string>;
  serviceId: string;
}

/**
 * `POST /servicedesk/{serviceType}/bulk/delete/assignee` — deletes N schedule
 * rows in one call.
 *
 * Partial success is a 200: the response splits into `succeeded[]` and
 * `failed[]`, so a caller must inspect the body rather than trusting the status.
 */
export interface BulkDeleteSchedulesInput {
  serviceType: "ticket" | "request" | "task" | "project";
  serviceId: string;
  /** Same ids the single-delete route takes as a URL param. Deduped server-side. */
  scheduleIds: Array<string>;
}

export interface BulkDeleteSchedulesResult {
  succeeded: Array<{ schedule_id: string; data?: unknown }>;
  failed: Array<{ schedule_id: string; error: string }>;
}

export interface UpdateWatchListInput {
  serviceType: "ticket" | "request" | "task" | "project";
  serviceId: string;
}
export interface RemovePinServiceInput {
  serviceType: "ticket" | "request" | "task" | "project";
  serviceId: string;
}
export interface AddPinServiceInput extends RemovePinServiceInput {
  colorCodePrimary: string;
  colorCodeTint: string;
}

export interface UpdateTicketKnowledgeBaseInput {
  ticketId: string;
  knowledgeBase: {
    mapping_id: string;
    content: string;
  };
}

export interface DeleteTicketKnowledgeBaseInput {
  ticketId: string;
  knowledgeBaseId: string;
}

export interface AddTicketKnowledgeBaseInput {
  ticketId: string;
  knowledgeBase: {
    content?: string;
    type: "link" | "note";
    links?: Array<{
      content: string;
      source?: string;
      doc_id?: string;
      is_external?: boolean;
    }>;
  };
}
export interface TicketKnowledgeBaseResponse {
  id: string;
  mapping_id: string;
  title: string;
  content: string;
  type: "link" | "note";
  msp_id: string;
  service_id: string;
  is_deleted: boolean;
  source: string;
  doc_id: string;
  is_external: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
}

export interface GetTicketByIdResponse {
  mapping_id: string;
  msp_id: string;
  u_id: string;
  is_expired_sla: boolean;
  ticket_owner: string;
  ticket_owner_profile_image: string | null;
  ticket_owner_first_name: string | null;
  ticket_owner_last_name: string | null;
  ticket_owner_display_name: string | null;
  merged_to_mapping_id: string | null;
  merged_to_uid: string | null;
  child_to_mapping_id: string | null;
  child_to_uid: string | null;
  ticket_summary: string;
  ticket_description: string;
  ticket_status: string;
  status_name: string;
  priority: string;
  urgency: string | null;
  impact: string | null;
  connect_time_from: string | null;
  connect_time_to: string | null;
  is_billable: boolean;
  is_archived: boolean;
  budgeted_hours: Record<string, any>;
  scheduled_hours: Record<string, any>;
  actual_hours: number | null;
  board_id: string;
  board_name: string;
  estimated_end_date: string | null;
  work_type: string | null;
  work_type_name: string | null;
  work_role: string | null;
  work_role_name: string | null;
  source: string;
  source_name: string;
  location: string | null;
  location_name: string | null;
  sla: string | null;
  sla_name: string | null;
  feedback: string | null;
  rating: string | null;
  ticket_type_id: string | null;
  type_name: string | null;
  ticket_subtype_id: string | null;
  subtype_name: string | null;
  customer_id: string;
  customer_name: string;
  customer_logo: string | null;
  customer_email: string;
  customer_contact_number: string;
  customer_code: string;
  site_id: string;
  site_name: string;
  ticket_site_id: string;
  ticket_site_name: string;
  street: string;
  city: string;
  country: string;
  state: string;
  zip_code: string;
  customer_user_id: string;
  customer_user_profile_image: string;
  customer_user_first_name: string;
  customer_user_last_name: string;
  customer_user_display_name: string;
  customer_user_email: string;
  customer_user_phone_number: string;
  is_privileged_user: boolean;
  created_at: string;
  user_department: string;
  user_designation: string;
  contract_id: string;
  contract_name: string;
  created_by: string;
  created_by_first_name: string;
  created_by_middle_name: string;
  created_by_last_name: string;
  end_date: string | null;
  due_date: string | null;
  bill_after_closed: boolean;
  bill_products: boolean;
  bill_expenses: boolean;
  ticketVariableAttribute: any[];
  generalVariableAttribute: any[];
  checklists: any[];
  childTickets: any[];
  attachments: any[];
  mergeTickets: any[];
  schedules: ScheduleResult[];
  tags: any[];
  ticketTypeSubtype: any[];
  timeEntries: any[];
  products: any[];
  expenses: any[];
  knowledgeBases: TicketKnowledgeBaseResponse[];
  escalatedMembers?: TicketEscalatedMember[];
}

interface GetAllTicketsAssignee {
  assignee_mapping_id: string;
  assignee_first_name: string;
  assignee_last_name: string;
  assignee_email: string;
  assignee_profile_image: string | null;
}

interface GetAllTicketsSlaRule {
  sla_name: string;
  sla_priority: string | null;
  sla_first_response: string | null;
  sla_resolution_time: string | null;
  sla_warning_time: string | null;
}

// Main ticket interface
export interface TicketObjectResponse {
  mapping_id: string;
  u_id: string;
  ticket_summary: string;
  ticket_description: string;
  ticket_status: string;
  ticket_type: string;
  ticket_subtype: string;
  priority: "low" | "medium" | "high" | "critical" | string;
  urgency: "low" | "medium" | "high" | "critical" | string | null;
  impact: "low" | "medium" | "high" | "critical" | string | null;
  connect_time_from: string | null;
  connect_time_to: string | null;
  is_billable: boolean;
  budgeted_hours: Hours | Record<string, never>;
  scheduled_hours: Hours | null;
  actual_hours: Hours | Record<string, never> | null;
  board_id: string;
  estimated_end_date: string | null;
  work_type: string | null;
  work_role: string | null;
  location_id: string | null;
  is_expired_sla: boolean;
  sla: string;
  created_at: string;
  customer_id: string;
  customer_user_id: string;
  msp_id: string;
  customer_name: string;
  customer_logo: string | null;
  customer_email: string | null;
  is_privileged_user: boolean;
  customer_user_first_name: string;
  customer_user_last_name: string;
  customer_user_email: string;
  customer_user_profile_image: string | null;
  board_name: string;
  type_name: string;
  subtype_name: string;
  status_name: string;
  assignees: GetAllTicketsAssignee[];
  sla_rules: GetAllTicketsSlaRule[];
  site_id: string;
  site_name: string;
  ticket_site_id: string;
  ticket_site_name: string;
  sla_tracking?: SlaTrackingData | null;
  ticket_agents?: { agent_type: string; status: string }[] | null;
  current_sentiment?: "positive" | "neutral" | "negative" | null;
}

// Response interface
export interface GetAllTicketsResponse {
  totalCount: number;
  totalPages: number;
  result: TicketObjectResponse[];
}

// Dispatch portal — unassigned-ticket queue.
// GET /v1/servicedesk/unassigned-tickets (validated 2026-07-16).
// Unlike the UUID-mapped ticket list, this endpoint ships labels directly:
// `priority`, `status_name`, `board_name`, `urgency` are human labels — no
// client UUID→label lookup is needed. (`board_id`/`ticket_status`/`sla` remain
// UUIDs, but each carries a paired label.)
export interface UnassignedTicketSlaRule {
  sla_name: string;
  sla_priority: string;
  /** Human-readable durations, e.g. "15 minutes". */
  sla_first_response: string;
  sla_resolution_time: string;
  sla_warning_time: string;
}

export interface UnassignedTicketSlaTracking {
  firstResponseStatus: string;
  firstResponseTargetMinutes: number;
  firstResponseElapsedMinutes: number;
  firstResponseCompletedAt: string | null;
  resolutionStatus: string;
  resolutionTargetMinutes: number;
  resolutionElapsedMinutes: number;
  resolutionCompletedAt: string | null;
  escalationLevelTriggered: number;
}

export interface UnassignedTicket {
  mapping_id: string;
  u_id: string;
  ticket_summary: string;
  /** May contain HTML — sanitize before rendering. */
  ticket_description: string;
  board_id: string | null;
  board_name: string | null;
  priority: string;
  urgency: string | null;
  customer_id: string;
  customer_name: string;
  ticket_status: string;
  status_name: string;
  is_expired_sla: boolean;
  sla: string | null;
  first_response_sla_status: string | null;
  resolution_sla_status: string | null;
  created_at: string;
  customer_email: string | null;
  is_privileged_user: boolean;
  user_type: string | null;
  customer_user_first_name: string;
  customer_user_last_name: string;
  customer_user_email: string;
  customer_user_profile_image: string | null;
  sla_name: string | null;
  /** May be an empty array when the ticket has no SLA plan. */
  sla_rules: UnassignedTicketSlaRule[];
  sla_tracking: UnassignedTicketSlaTracking | null;
}

export interface GetUnassignedTicketsQueryProps {
  /** 1-based page. Sent as `page`. */
  pageNumber?: number;
  /** Rows per page. Sent as `per_page`. */
  perPageCount?: number;
  /** Free-text search over the queue. Sent as `searchQuery`. */
  searchQuery?: string;
  /**
   * Filter to these board ids (`ticket_board` mapping_ids). Serialized into the
   * tickets-list `filters` contract: `[{ column: "board", operator: "=", value }]`.
   */
  boardIds?: string[];
  /**
   * The DP-17 queue filter, each serialized into the SAME `filters` array as
   * `boardIds` and AND-ed with it.
   *
   * VERIFIED on UAT 2026-10-01: every column below is honoured, predicates AND
   * correctly, and an unrecognised column returns 400 rather than silently
   * returning unfiltered rows.
   *
   * `slaStatuses` has a known semantic mismatch: the backend matches the
   * resolution clock alone, the UI labels by the worse of both clocks. See Ask 4
   * in .claude/projects/control-center/dispatch-portal/dispatch-portal-v2/dp-17/dispatch-unassigned-queue-filter-backend-api-requirements.md
   */
  priorities?: string[];
  ticketStatusIds?: string[];
  slaStatuses?: string[];
  /** Verified honoured on UAT 2026-10-01 (`created_at`, `priority`; asc ≠ desc). */
  sortColumn?: string;
  sortDirection?: "asc" | "desc";
}

export interface GetUnassignedTicketsResponse {
  /** Backend sends this as a STRING (unlike GetAllTicketsResponse.totalCount). */
  totalCount: string;
  totalPages: number;
  result: UnassignedTicket[];
}
